import { TanStackDevtools } from "@tanstack/react-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import {
	ClientOnly,
	HeadContent,
	Outlet,
	Scripts,
	createRootRouteWithContext,
	redirect,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

import { Header } from "@/components/header.tsx";

import appCss from "@/styles.css?url";

import type { QueryClient } from "@tanstack/react-query";

import { Footer } from "@/components/footer";
import { deleteToken__ServerOnly } from "@/lib/backend/auth";
import { seo } from "@/lib/seo";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime.js";
import { DefaultErrorComponent } from "@bidbattles/bloks/components/errors";
import { Toaster } from "@bidbattles/bloks/components/ui/toaster";
import { BBBackendQueryOptions } from "@bidbattles/bloks/lib/rapace";
import { BBBackendError } from "@bidbattles/bloks/lib/rapace/utils";
import { Result } from "@bidbattles/bloks/lib/result";
import { createServerFn } from "@tanstack/react-start";

interface MyRouterContext {
	queryClient: QueryClient;
}

const logout__ServerFn = createServerFn().handler(
	async (): Promise<Result<null, never>> => {
		deleteToken__ServerOnly();
		return Result.ok(null);
	},
);

export const Route = createRootRouteWithContext<MyRouterContext>()({
	loader: async ({ context }) => {
		const [userPromise, _] = await Promise.allSettled([
			context.queryClient
				.ensureQueryData(BBBackendQueryOptions.me())
				.then((user) => {
					if (!user) {
						throw new BBBackendError("unauthorized");
					}
					return user;
				}),
			context.queryClient.ensureQueryData(
				BBBackendQueryOptions.listSubscriptionTiers(),
			),
		]);

		if (userPromise.status === "rejected") {
			if (
				userPromise.reason instanceof BBBackendError &&
				userPromise.reason.message === "unauthorized"
			) {
				await logout__ServerFn().catch((error) => {
					console.warn("[auth] deauth failed", { error });
				});
				// throw redirect({
				// 	to: "/",
				// });
				return;
			}

			throw userPromise.reason;
		}
	},
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			...seo({
				title: m.bidbattles(),
				description: m.seo__description(),
			}),
		],
		links: [
			{
				rel: "favicon",
				href: "/favicon.ico",
				sizes: "48x48",
			},
			{
				rel: "icon",
				href: "/icon.svg",
				sizes: "any",
				type: "image/svg+xml",
			},
			{
				rel: "apple-touch-icon",
				href: "/apple-touch-icon-180x180.png",
			},
			{
				rel: "manifest",
				href: "/manifest.webmanifest",
			},
			{
				rel: "stylesheet",
				href: appCss,
			},
		],
	}),

	shellComponent: RootDocument,

	// component: () => {
	// 	return (
	// 		<RootDocument>
	// 			<Header />
	// 			<Outlet />
	// 			<Footer />

	// 			<TanStackRouterDevtools />
	// 			<TanStackQueryLayout />
	// 		</RootDocument>
	// 	);
	// },

	errorComponent: (props) => (
		<html lang={getLocale()}>
			<head>
				<HeadContent />
			</head>
			<body>
				<div
					id="app"
					className="flex flex-col min-h-screen bg-white isolate relative overflow-auto"
					data-vaul-drawer-wrapper=""
				>
					<DefaultErrorComponent {...props} />
				</div>
				<Devtools />
				<ClientOnly>
					<Toaster />
				</ClientOnly>
				<Scripts />
			</body>
		</html>
	),
});

const Devtools = () => (
	<TanStackDevtools
		config={{
			position: "bottom-right",
		}}
		plugins={[
			{
				name: "Tanstack Router",
				render: <TanStackRouterDevtoolsPanel />,
			},
			{
				name: "Tanstack Query",
				render: <ReactQueryDevtoolsPanel />,
			},
		]}
	/>
);

function RootDocument({ children }: { children: React.ReactNode }) {
	return (
		<html lang={getLocale()}>
			<head>
				<HeadContent />
			</head>
			<body>
				<div
					id="app"
					className="flex flex-col min-h-screen bg-white isolate relative overflow-auto"
					data-vaul-drawer-wrapper=""
				>
					<Header />
					{children}
					<Footer />
				</div>
				<Devtools />
				<ClientOnly>
					<Toaster />
				</ClientOnly>
				<Scripts />
			</body>
		</html>
	);
}
